/**
 * v2.9 详情页"画面"Tab 的滤镜预设。chip 单选互斥,选中后整组参数生效。
 *
 * 数值跟 ai-service compositor.apply_filter 对齐:
 * brightness/contrast/saturation/warmth ∈ [-50, 50]。
 *
 * 设计原则:小白用户看不懂"对比度",所以我们只暴露 6 个 chip(默认/明亮/鲜艳/柔和/暖/冷)。
 * 每个 chip 是预先调好的"看起来好看"参数组合。"高级调整"折叠面板才暴露原始参数。
 */
import type { FilterConfig } from "../../../api/posterService";

export type FilterPresetId = "default" | "bright" | "vivid" | "soft" | "warm" | "cool";

export interface FilterPreset {
  id: FilterPresetId;
  label: string;
  /** 给 hover/long-press tooltip,小白判断"我想要这种" */
  description: string;
  params: FilterConfig;
}

export const FILTER_PRESETS: FilterPreset[] = [
  {
    id: "default",
    label: "默认",
    description: "AI 出图原貌,无任何调整",
    params: { brightness: 0, contrast: 0, saturation: 0, warmth: 0 },
  },
  {
    id: "bright",
    label: "明亮",
    description: "整体提亮,适合朋友圈传播",
    params: { brightness: 15, contrast: 10, saturation: 0, warmth: 0 },
  },
  {
    id: "vivid",
    label: "鲜艳",
    description: "色彩更饱和,视觉冲击更强",
    params: { brightness: 0, contrast: 10, saturation: 25, warmth: 0 },
  },
  {
    id: "soft",
    label: "柔和",
    description: "降低反差,氛围更温和",
    params: { brightness: 0, contrast: -10, saturation: -15, warmth: 0 },
  },
  {
    id: "warm",
    label: "暖色",
    description: "整体偏暖,氛围温馨",
    params: { brightness: 5, contrast: 0, saturation: 5, warmth: 20 },
  },
  {
    id: "cool",
    label: "冷色",
    description: "整体偏冷,氛围科技或清爽",
    params: { brightness: 0, contrast: 5, saturation: 0, warmth: -20 },
  },
];

/** 文字大小三档 → font_size_offset。spec §11 容忍 5% 像素差。 */
export const FONT_SIZE_PRESETS = [
  { id: "small", label: "小", offset: -4 },
  { id: "medium", label: "中", offset: 0 },
  { id: "large", label: "大", offset: 4 },
] as const;

export type FontSizeId = (typeof FONT_SIZE_PRESETS)[number]["id"];

/** 把现有 filter_config 反推回最接近的 chip,用于初始 chip 高亮。 */
export function detectActivePreset(filter: FilterConfig | null | undefined): FilterPresetId {
  if (!filter) return "default";
  for (const preset of FILTER_PRESETS) {
    const p = preset.params;
    if (
      (filter.brightness ?? 0) === (p.brightness ?? 0) &&
      (filter.contrast ?? 0) === (p.contrast ?? 0) &&
      (filter.saturation ?? 0) === (p.saturation ?? 0) &&
      (filter.warmth ?? 0) === (p.warmth ?? 0)
    ) {
      return preset.id;
    }
  }
  return "default"; // 自定义高级参数时回到默认 chip 状态
}

/** 从 font_size_offset 反推回 chip。 */
export function detectFontSize(filter: FilterConfig | null | undefined): FontSizeId {
  const offset = filter?.font_size_offset ?? 0;
  if (offset <= -2) return "small";
  if (offset >= 2) return "large";
  return "medium";
}

/** 三档大小 → size_ratio。logo / qrcode 用。 */
export const SIZE_RATIO_PRESETS = {
  small: 0.08,
  medium: 0.12,
  large: 0.16,
} as const;

export type SizeRatioId = keyof typeof SIZE_RATIO_PRESETS;

export function detectSizeRatio(ratio: number | undefined): SizeRatioId {
  if (ratio == null) return "medium";
  if (ratio <= 0.1) return "small";
  if (ratio >= 0.15) return "large";
  return "medium";
}
